'use client'

;!function(){try{var e="undefined"!=typeof globalThis?globalThis:"undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:offsoo-landing":true});}catch(e){}}();import { useEffect, useRef, useState } from 'react'

import {
  advanceRowSpotlight,
  holdsSpotlight,
  registerSpotlightParticipant,
  type SpotlightParticipant,
} from './spotlight'

const DEFAULT_INTERVAL_MS = 1000

/**
 * How long an activated visual waits before its first move.
 *
 * The loop enters at `restBeat`, which renders what is already on screen — so this delay is the
 * whole of the wait a visitor experiences, and the entering beat's own hold must NOT be added to
 * it. Charging the full hold on entry meant hovering Excel sat still for 450ms plus a 2200ms rest
 * beat before anything moved, which reads as the animation refusing to start.
 *
 * Short enough that a deliberate hover feels answered immediately, long enough that a spotlight
 * passing through during a fast scroll does not visibly disturb the resting frame.
 */
const INITIAL_START_MS = 220

/**
 * What the three product-page heroes wait instead.
 *
 * A card in a row is activated by a pointer that went looking for it, and it owes that pointer an
 * answer inside a fifth of a second. A hero is activated by the page being opened: nobody asked it
 * for anything, the frame it is resting on is the first picture of the product anyone sees, and at
 * 220ms it is gone before it has been looked at — the page appears to change under the reader.
 *
 * Long enough to read the frame, short enough that the card is plainly alive by the time the eye
 * has finished the headline beside it.
 */
export const HERO_ENTER_MS = 1600

/**
 * How long each beat stays on screen: a beat count, meaning every beat is held equally long, or one
 * hold time in milliseconds per beat.
 *
 * The per-beat form is what gives a visual rhythm. A beat that reveals a dialog needs time to be
 * read; a beat that ticks a counter does not; and a beat in which nothing happens should not exist.
 * One shared interval could express none of that, which is why every story used to move at the same
 * pace regardless of what it was showing.
 */
export type BeatSchedule = number | readonly number[]

/**
 * Drives a looping "beat" counter (0..beatCount-1) for a continuously-animated scenario visual.
 *
 * `beat` starts `null` — SSR, first paint, and `prefers-reduced-motion: reduce` all stay `null`
 * forever. Callers MUST treat `beat === null` as the RESTING state (usually a `REST_BEAT` snapshot
 * of a finished scenario), so no-JS, pre-hydration and reduced-motion all render something complete.
 *
 * Exactly one scenario on the page plays at a time: each hook registers with the spotlight
 * registry (see spotlight.ts), which hands the single spotlight to the most prominent participant
 * in the viewport, with hover overriding on hover-capable devices. While a visual holds the
 * spotlight its root carries `data-animating`, which is also what un-pauses the infinite CSS
 * micro-loops inside it.
 *
 * On activation the visual holds its resting frame for `INITIAL_START_MS` — or for `enterHoldMs`,
 * which the product-page heroes set, because a hero is activated by the page being opened rather
 * than by a pointer asking it for something — and then enters the loop AT that same frame:
 * `restBeat`, which callers set to whichever beat renders what `beat === null` renders. Entering
 * anywhere else shows the visitor a different picture before the story starts: with a visual
 * resting on its opening frame, entering at the final beat flashed the ending, then the beginning,
 * then played the story. Excel went file → board → file → build.
 * On losing the spotlight the visual returns to its resting frame immediately; the visuals' own
 * CSS transitions turn that state change into a smooth settle rather than a hard cut.
 *
 * While playing, the root also carries `data-beat` with the current index. That is what makes a
 * visual's pacing observable — to a test, and to anyone inspecting why a beat feels wrong — rather
 * than something to be inferred from watching pixels.
 */
export function useScenario(
  schedule: BeatSchedule,
  opts?: {
    intervalMs?: number
    restBeat?: number
    /** How long the resting frame is held on activation, before the first move. See `HERO_ENTER_MS`. */
    enterHoldMs?: number
  },
) {
  const intervalMs = opts?.intervalMs ?? DEFAULT_INTERVAL_MS
  const enterHoldMs = opts?.enterHoldMs ?? INITIAL_START_MS
  const holds = typeof schedule === 'number' ? Array.from({ length: schedule }, () => intervalMs) : [...schedule]
  // Depend on the schedule's contents, not its identity: a visual may pass an inline array, and a
  // fresh reference every render would otherwise restart the loop on every render.
  const holdsKey = holds.join(',')
  // Which beat renders what `beat === null` renders. The loop enters there, so activating a visual
  // continues from the frame already on screen instead of jumping somewhere else first.
  const restBeat = opts?.restBeat ?? holds.length - 1
  const ref = useRef<HTMLDivElement>(null)
  const [beat, setBeat] = useState<number | null>(null)

  useEffect(() => {
    const node = ref.current
    if (!node) return

    const beatHolds = holdsKey.split(',').map(Number)
    const beatCount = beatHolds.length

    let stepTimer: ReturnType<typeof setTimeout> | null = null
    let startTimer: ReturnType<typeof setTimeout> | null = null

    const stop = () => {
      if (startTimer !== null) {
        clearTimeout(startTimer)
        startTimer = null
      }
      if (stepTimer !== null) {
        clearTimeout(stepTimer)
        stepTimer = null
      }
    }

    // Tracked here rather than read back from `beat`, so a completed pass is detected off one
    // authoritative counter instead of off a state updater React is free to run twice.
    let current = restBeat

    const show = (next: number) => {
      current = next
      setBeat(next)
      node.setAttribute('data-beat', String(next))
    }

    // A timeout chain rather than an interval: the wait before the next beat is the current beat's
    // own hold time, which is the whole point of the schedule.
    const advance = () => {
      show((current + 1) % beatCount)
      // Back at the beat the loop entered on: the story has played through once. Offer the
      // spotlight to the next visual in this row, if this visual shares its row with any —
      // see `advanceRowSpotlight`. Alone in its row, this is a no-op and the loop runs on.
      if (current === restBeat) advanceRowSpotlight(participant)
    }

    // Re-arming is conditional, and that condition is the whole reason a row of visuals does not
    // end up all playing at once. `advance()` can hand the spotlight to a neighbour on its closing
    // beat; that hands this visual its own `onDeactivate`, which clears the pending timer — and then
    // an unconditional `step()` immediately set a new one. Every visual that ever completed a pass
    // kept ticking from then on, so a three-card row had all three running after one round, and they
    // carried on after the visitor scrolled away.
    const step = () => {
      if (!holdsSpotlight(participant)) return
      stepTimer = setTimeout(() => {
        advance()
        step()
      }, beatHolds[current])
    }

    const participant: SpotlightParticipant = {
      element: node,
      onActivate: () => {
        node.setAttribute('data-animating', '')
        // Shown immediately — it is the frame already in view, so there is nothing to reveal — and
        // the first real move is scheduled off the entry delay rather than off this beat's hold.
        show(restBeat)
        startTimer = setTimeout(() => {
          startTimer = null
          advance()
          step()
        }, enterHoldMs)
      },
      onDeactivate: () => {
        stop()
        // Removing `data-animating` is what takes this visual out of the story: a global rule
        // normalises every transition inside a registered visual that is not holding the spotlight
        // to one eased duration, and stops its keyframe loops outright. The state change below then
        // travels back to the resting frame over that one duration, so a visual that stops eases
        // back to where it started instead of blinking there — and the spotlight waits it out
        // before starting the next one. See globals.css.
        node.removeAttribute('data-animating')
        node.removeAttribute('data-beat')
        setBeat(null)
      },
    }

    const unregister = registerSpotlightParticipant(participant)

    return () => {
      unregister()
      stop()
    }
  }, [holdsKey, restBeat, enterHoldMs])

  return { ref, beat }
}
